/**
 * Houdini — „Houdini vs. The Genie" (súťaž dizajnérov Restoration Games).
 *
 * Kúzelník s kartami: veľa kariet má „BOOSTED WITH" efekt, ktorý sa spustí,
 * keď sa karta **odhodí za boost** (v manévri aj v súboji). Schopnosť ESCAPE
 * ARTIST mu po booste v manévri dovolí namiesto pohybu skočiť kamkoľvek.
 */

import type { Ctx } from '../ops.js';
import { askChoice, drawCard, gainAction, healFighter, heroOf } from '../ops.js';
import type { FighterId, PlayerIndex } from '../types.js';
import { askBoost, askMove, fighterOrNull, opponentOf, revealHand } from './helpers.js';
import type { CardEffect, EffectCtx, EffectRegistry, HeroSpecial } from './types.js';

const MAGICIAN_DUO = new Set(['houdini', 'bess']);

/** Prázdne priestory — „place your fighter in any space". */
function emptySpaces(ctx: Ctx): string[] {
  const map = ctx.content.maps[ctx.state.mapId];
  return (map?.spaces ?? []).map((s) => s.id).filter((id) => !ctx.state.fighters.some((f) => f.spaceId === id));
}

function askPlaceAnywhere(ctx: Ctx, player: PlayerIndex, source: string | null, fighterId: FighterId, label: string) {
  askChoice(ctx, {
    kind: 'place',
    player,
    source,
    label,
    optional: false,
    fighterId,
    spaces: emptySpaces(ctx),
  });
}

const drawOneWhenBoosted = (e: EffectCtx) => {
  drawCard(e.ctx, e.player);
};

/**
 * „Choose one of the fighters in the combat and move them up to 3 spaces.
 * They may move through their opposing fighters."
 */
const vanishingAct: CardEffect = {
  after: (e) => {
    const inCombat = [e.fighterId, e.opponentFighterId].filter((id): id is string => Boolean(id));
    askMove(e, {
      fighters: inCombat,
      max: 3,
      through: true,
      label: 'Vanishing Act: presuň jednu figúrku zo súboja až o 3 (smie prejsť cez súperov)',
    });
  },
};

/** „DURING COMBAT: You may BOOST this card twice. AFTER: Return one card you boosted with to your hand." */
const theBigReveal: CardEffect = {
  duringChoice: (e) => {
    askBoost(e, 'The Big Reveal: prvý boost (smieš dvakrát)');
    askBoost(e, 'The Big Reveal: druhý boost');
  },
  after: (e) => {
    const cards = e.ctx.state.combat?.boostCards?.[e.role === 'defender' ? 'defender' : 'attacker'] ?? [];
    if (cards.length === 0) return;
    askChoice(e.ctx, {
      kind: 'card',
      player: e.player,
      source: e.cardId,
      label: 'The Big Reveal: vráť do ruky jednu kartu, ktorou si boostoval',
      optional: false,
      from: 'discard',
      cards: cards.slice(),
      purpose: 'toHand',
    });
  },
};

/** „IMMEDIATELY: Houdini and Bess may swap spaces. If they do, your other fighter is now in the combat." */
const smokeAndMirrors: CardEffect = {
  immediate: (e) => {
    const fighter = fighterOrNull(e.ctx, e.fighterId);
    if (!fighter || !MAGICIAN_DUO.has(fighter.defId)) return;
    const partner = e.ctx.state.fighters.find(
      (f) => f.owner === e.player && f.id !== fighter.id && MAGICIAN_DUO.has(f.defId),
    );
    if (!partner) return;
    askChoice(e.ctx, {
      kind: 'swap',
      player: e.player,
      source: e.cardId,
      label: `Smoke and Mirrors: vymeniť ${fighter.name} s ${partner.name}? Do súboja pôjde ${partner.name}.`,
      optional: true,
      fighterId: fighter.id,
      candidates: [partner.id],
    });
  },
};

/**
 * „IMMEDIATELY: You may return this card to your hand and choose a different
 * card to play. Resolve all effects on that card as normal." BOOSTED WITH: draw 1.
 */
const sleightOfHand: CardEffect = {
  immediate: (e) => {
    if (e.role !== 'attacker' || !e.fighterId) return;
    askChoice(e.ctx, {
      kind: 'card',
      player: e.player,
      source: e.cardId,
      label: 'Sleight of Hand: smieš túto kartu vrátiť do ruky a zahrať namiesto nej inú',
      optional: true,
      from: 'hand',
      purpose: 'replace',
      playableBy: e.fighterId,
    });
  },
  boosted: drawOneWhenBoosted,
};

/**
 * „Draw 3 cards. Then, put a card in your hand on top of your deck."
 * BOOSTED WITH: „Look at an opponent's hand and choose a card for them to discard."
 */
const setTheStage: CardEffect = {
  scheme: (e) => {
    for (let i = 0; i < 3; i++) drawCard(e.ctx, e.player);
    askChoice(e.ctx, {
      kind: 'card',
      player: e.player,
      source: e.cardId,
      label: 'Set the Stage: polož kartu z ruky navrch balíčka',
      optional: false,
      from: 'hand',
      purpose: 'topOfDeck',
    });
  },
  boosted: (e) => {
    const opponent = opponentOf(e);
    if (opponent === null || (e.ctx.state.players[opponent]?.hand.length ?? 0) === 0) return;
    if (!revealHand(e, opponent)) return;
    askChoice(e.ctx, {
      kind: 'card',
      player: e.player,
      source: e.cardId,
      label: 'Set the Stage: vyber kartu, ktorú súper odhodí',
      optional: false,
      from: 'hand',
      owner: opponent,
      purpose: 'discard',
    });
  },
};

/** „IMMEDIATELY: Cancel all effects on your opponent's card." BOOSTED WITH: „Deal 2 damage to any fighter." */
const misdirection: CardEffect = {
  cancelsOpponent: true,
  boosted: (e) => {
    askChoice(e.ctx, {
      kind: 'damage',
      player: e.player,
      source: e.cardId,
      label: 'Misdirection (boost): 2 poškodenia ľubovoľnej figúrke',
      optional: false,
      amount: 2,
      targets: e.ctx.state.fighters.map((f) => f.id),
    });
  },
};

/** „DURING: You may BOOST this card. AFTER: If you won the combat, place your fighter in any space." */
const grandEscape: CardEffect = {
  duringChoice: (e) => askBoost(e, 'Grand Escape: smieš boostovať obranu'),
  after: (e) => {
    if (!e.won || !e.fighterId) return;
    askPlaceAnywhere(e.ctx, e.player, e.cardId, e.fighterId, 'Grand Escape: polož figúrku na ľubovoľný priestor');
  },
};

/** „DURING COMBAT: You may BOOST this card." BOOSTED WITH: draw 1. */
const flourish: CardEffect = {
  duringChoice: (e) => askBoost(e, 'Flourish: smieš boostovať útok'),
  boosted: drawOneWhenBoosted,
};

/** „IMMEDIATELY: Your opponent may BOOST their card. DURING COMBAT: You may BOOST this card." */
const anIllusion: CardEffect = {
  immediate: (e) => {
    const combat = e.ctx.state.combat;
    if (!combat?.defenseCard || e.opponentPlayer === null) return;
    if ((e.ctx.state.players[e.opponentPlayer]?.hand.length ?? 0) === 0) return;
    askChoice(e.ctx, {
      kind: 'card',
      player: e.opponentPlayer,
      source: e.cardId,
      label: 'An Illusion of My Own Design: smieš boostovať svoju kartu',
      optional: true,
      from: 'hand',
      purpose: 'boost',
      side: 'defender',
    });
  },
  duringChoice: (e) => askBoost(e, 'An Illusion of My Own Design: smieš boostovať útok'),
};

/**
 * „This card's effect cannot be canceled. AFTER COMBAT: If your fighter was
 * defeated, set their health to 4 instead and place them in any space."
 * BOOSTED WITH: „Houdini recovers 2 health."
 */
const allPartOfTheShow: CardEffect = {
  uncancelable: true,
  surviveLethal: (e) => {
    e.ctx.state.combat?.flags?.push('rescued');
    return 4;
  },
  after: (e) => {
    if (!e.fighterId || !e.ctx.state.combat?.flags?.includes('rescued')) return;
    askPlaceAnywhere(e.ctx, e.player, e.cardId, e.fighterId, 'All Part of the Show: polož figúrku na ľubovoľný priestor');
  },
  boosted: (e) => {
    const houdini = heroOf(e.ctx.state, e.player);
    if (houdini) healFighter(e.ctx, houdini.id, 2);
  },
};

/**
 * „Draw 2 cards. Gain 1 action." Zrušenie nazretia do ruky rieši `revealHand`
 * v helpers.ts — karta tam stačí mať v ruke.
 */
const aMagicianNeverReveals: CardEffect = {
  scheme: (e) => {
    drawCard(e.ctx, e.player);
    drawCard(e.ctx, e.player);
    gainAction(e.ctx, e.player);
  },
};

/** „Move one of your fighters up to 1 space. Draw 1 card. Gain 1 action." */
const forMyNextTrick: CardEffect = {
  after: (e) => {
    askMove(e, {
      fighters: e.ctx.state.fighters.filter((f) => f.owner === e.player).map((f) => f.id),
      max: 1,
      label: 'For My Next Trick: presuň jednu svoju figúrku o 1 priestor',
    });
    drawCard(e.ctx, e.player);
    gainAction(e.ctx, e.player);
  },
};

/**
 * „If you discard this card as a result of an opponent's effect, draw 1 card."
 * BOOSTED WITH: „If it's your turn, gain 1 action."
 */
const andTheBeautifulBess: CardEffect = {
  onDiscardedByOpponent: (ctx, player) => {
    drawCard(ctx, player);
  },
  boosted: (e) => gainAction(e.ctx, e.player),
};

export const houdiniCards: EffectRegistry = {
  'harry-houdini.vanishing-act': vanishingAct,
  'harry-houdini.the-big-reveal': theBigReveal,
  'harry-houdini.smoke-and-mirrors': smokeAndMirrors,
  'harry-houdini.sleight-of-hand': sleightOfHand,
  'harry-houdini.set-the-stage': setTheStage,
  'harry-houdini.misdirection': misdirection,
  'harry-houdini.grand-escape': grandEscape,
  'harry-houdini.flourish': flourish,
  'harry-houdini.an-illusion-of-my-own-design': anIllusion,
  'harry-houdini.all-part-of-the-show': allPartOfTheShow,
  'harry-houdini.a-magician-never-reveals-his-secrets': aMagicianNeverReveals,
  'harry-houdini.for-my-next-trick': forMyNextTrick,
  'harry-houdini.and-the-beautiful-bess': andTheBeautifulBess,
};

/**
 * ESCAPE ARTIST — „When you take the maneuver action and BOOST, you may place
 * Houdini in any space instead of moving. (Bess moves as normal.)"
 */
export const houdiniSpecial: HeroSpecial = {
  name: 'Escape Artist',
  maneuverPlace: (_ctx, _player, fighter) => fighter.defId === 'houdini',
};
